import type {
  AhqSubmission,
  AhqSubmissionCandidateExperience,
  Candidate,
  Opportunity,
  OpportunityCandidate,
} from '@axiom/validation';
import { CandidateOpportunitiesConst } from '@axiom/const';

import { CandidateRateUtil } from '../../utils/candidate-rate-util';
import { CandidateUtil } from '../../utils/candidate-util';
import { SubmissionsApi } from '../../api/submissions-api';
import { OpportunityLegacyApi } from '../../api/opportunity-legacy-api';
import { CandidateApi } from '../../api/candidate-api';

const { CandidateStatuses } = CandidateOpportunitiesConst;

const SUBMISSION_CANDIDATE_ALLOWED_SUBMIT_STATUSES = new Set([
  CandidateStatuses.WarmedYes,
  CandidateStatuses.Submitted,
  CandidateStatuses.Interviewing,
  CandidateStatuses.Selected,
]);

const SUBMISSION_CANDIDATE_NEGATIVE_STATUSES = new Set([
  CandidateStatuses.Rejected,
  CandidateStatuses.Cooled,
]);

export const OpportunitySubmissionUtil = {
  isAfcSubmitted({
    candidate,
    opportunity,
  }: {
    candidate: OpportunityCandidate;
    opportunity: Opportunity;
  }): boolean {
    return CandidateUtil.isSubmittedFromWorkFeed(candidate, opportunity);
  },

  hasSubmittableDescription({
    candidate,
    opportunity,
  }: {
    candidate: OpportunityCandidate;
    opportunity: Opportunity;
  }): boolean {
    return (
      OpportunitySubmissionUtil.isCuratedTalent({
        candidate,
        opportunity,
      }) &&
      [CandidateStatuses.Submitted, CandidateStatuses.Interviewing].includes(
        candidate.candidateStatus
      )
    );
  },

  isCuratedTalent({
    candidate,
    opportunity,
  }: {
    candidate: OpportunityCandidate;
    opportunity: Opportunity;
  }): boolean {
    return (
      !OpportunitySubmissionUtil.isAfcSubmitted({ candidate, opportunity }) &&
      !!candidate.isActiveForSubmission
    );
  },

  isPendingCuratedTalent({
    candidate,
    opportunity,
  }: {
    candidate: OpportunityCandidate;
    opportunity: Opportunity;
  }): boolean {
    return (
      !OpportunitySubmissionUtil.isAfcSubmitted({ candidate, opportunity }) &&
      !OpportunitySubmissionUtil.isCuratedTalent({ candidate, opportunity })
    );
  },

  groupCandidates({
    candidates,
    opportunity,
  }: {
    candidates: OpportunityCandidate[];
    opportunity: Opportunity;
  }): {
    activeCuratedCandidates: OpportunityCandidate[];
    pendingCuratedCandidates: OpportunityCandidate[];
    afcSubmittedCandidates: OpportunityCandidate[];
  } {
    const activeCuratedCandidates: OpportunityCandidate[] = [];
    const pendingCuratedCandidates: OpportunityCandidate[] = [];
    const afcSubmittedCandidates: OpportunityCandidate[] = [];

    candidates.forEach(candidate => {
      if (
        OpportunitySubmissionUtil.isAfcSubmitted({ candidate, opportunity })
      ) {
        afcSubmittedCandidates.push(candidate);
      } else if (
        OpportunitySubmissionUtil.isCuratedTalent({ candidate, opportunity })
      ) {
        activeCuratedCandidates.push(candidate);
      } else {
        pendingCuratedCandidates.push(candidate);
      }
    });

    return {
      activeCuratedCandidates,
      pendingCuratedCandidates,
      afcSubmittedCandidates,
    };
  },

  getAllowedToSubmitCandidateErrorText(
    candidate: OpportunityCandidate
  ): string {
    if (SUBMISSION_CANDIDATE_NEGATIVE_STATUSES.has(candidate.candidateStatus)) {
      return "Can't be included because status is not appropriate.";
    }

    if (!CandidateRateUtil.hasSubmittedRate(candidate)) {
      return "Can't be included as rate isn't set.";
    }

    return SUBMISSION_CANDIDATE_ALLOWED_SUBMIT_STATUSES.has(
      candidate.candidateStatus
    )
      ? ''
      : "Can't be included because status is not appropriate.";
  },

  getYearsOfExperienceRange(yoe: number | null | undefined): string | null {
    if (yoe == null) return null;
    if (yoe >= 20) return '20+';
    if (yoe >= 16) return '16 - 19';
    if (yoe >= 12) return '12 - 15';
    if (yoe >= 8) return '8 - 11';
    return yoe >= 4 ? '4 - 7' : '0 - 3';
  },

  prepareExperienceForPatch(
    experience: AhqSubmissionCandidateExperience
  ): Pick<
    AhqSubmissionCandidateExperience,
    'id' | 'experienceVisible' | 'isHighlighted' | 'submissionDescription'
  > {
    return {
      id: experience.id,
      experienceVisible: experience.experienceVisible,
      isHighlighted: experience.isHighlighted,
      submissionDescription: experience.submissionDescription,
    };
  },

  buildSubmissionRankPatches({
    candidates,
    opportunity,
    reorderedCandidateIds,
    activatingCandidateId,
    deactivatingCandidateId,
  }: {
    candidates: OpportunityCandidate[];
    opportunity: Opportunity;
    reorderedCandidateIds?: string[] | null;
    activatingCandidateId?: string;
    deactivatingCandidateId?: string;
  }): Array<{
    candidateId: string;
    isActiveForSubmission: boolean | undefined;
    submissionRank: number | null;
  }> {
    const {
      activeCuratedCandidates,
      afcSubmittedCandidates,
      pendingCuratedCandidates,
    } = OpportunitySubmissionUtil.groupCandidates({ candidates, opportunity });

    const activatingCandidate = activatingCandidateId
      ? (pendingCuratedCandidates.find(c => c.id === activatingCandidateId) ??
        null)
      : null;

    const eligibleCuratedCandidates = deactivatingCandidateId
      ? activeCuratedCandidates.filter(c => c.id !== deactivatingCandidateId)
      : activeCuratedCandidates;

    const orderedCuratedCandidates = reorderedCandidateIds
      ? reorderedCandidateIds
          .map(id => eligibleCuratedCandidates.find(c => c.id === id))
          .filter((c): c is NonNullable<typeof c> => c !== undefined)
      : [...eligibleCuratedCandidates].sort(
          (a, b) =>
            (a.submissionRank ?? Infinity) - (b.submissionRank ?? Infinity)
        );

    const orderedAfcCandidates = [...afcSubmittedCandidates]
      .filter(c => !deactivatingCandidateId || c.id !== deactivatingCandidateId)
      .sort(
        (a, b) =>
          (a.submissionRank ?? Infinity) - (b.submissionRank ?? Infinity)
      );

    const allCandidatesInOrder = activatingCandidate
      ? [
          ...orderedCuratedCandidates,
          activatingCandidate,
          ...orderedAfcCandidates,
        ]
      : [...orderedCuratedCandidates, ...orderedAfcCandidates];

    const activePatches = allCandidatesInOrder
      .filter(
        c => c.isActiveForSubmission === true || c.id === activatingCandidateId
      )
      .map((c, index) => ({
        candidateId: c.id,
        isActiveForSubmission: true as const,
        submissionRank: index,
      }));

    const inactiveAfcPatches = orderedAfcCandidates
      .filter(c => c.isActiveForSubmission !== true)
      .map(c => ({
        candidateId: c.id,
        isActiveForSubmission: c.isActiveForSubmission ?? undefined,
        submissionRank: null,
      }));

    const inactiveCuratedPatches = pendingCuratedCandidates
      .filter(
        c => c.id !== deactivatingCandidateId && c.id !== activatingCandidateId
      )
      .map(c => ({
        candidateId: c.id,
        isActiveForSubmission: c.isActiveForSubmission ?? undefined,
        submissionRank: null,
      }));

    const deactivatingPatch = deactivatingCandidateId
      ? [
          {
            candidateId: deactivatingCandidateId,
            isActiveForSubmission: false as const,
            submissionRank: null,
          },
        ]
      : [];

    return [
      ...activePatches,
      ...inactiveAfcPatches,
      ...inactiveCuratedPatches,
      ...deactivatingPatch,
    ];
  },

  async refreshSubmissionAndCandidates({
    submissionId,
    opportunityId,
    candidateId,
  }: {
    submissionId: AhqSubmission['submissionId'];
    opportunityId: Opportunity['id'];
    candidateId?: Candidate['id'];
  }) {
    return Promise.all([
      SubmissionsApi.refreshSubmission(submissionId),
      OpportunityLegacyApi.refreshOpportunityCandidates(opportunityId),
      candidateId ? CandidateApi.readCandidate(candidateId) : Promise.resolve(),
    ]);
  },
};
